import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  MessageSquare, Phone, QrCode, LogOut, Send, RefreshCw, ArrowLeft, 
  Check, CheckCheck, User, Wifi, WifiOff, Loader2, Plus, X, Search,
  Users, MessageCircle, Image, Paperclip, FileText, CreditCard, 
  ShoppingBag, Wrench, UserPlus, Edit, Trash2, Save, Upload,
  AlertTriangle, Bell, Calendar, Gift, Heart, Star, Zap, Award,
  Briefcase, Clock, DollarSign, Mail, Settings, Shield, Truck, Home,
  CheckCircle, PhoneCall, Video, Megaphone, LayoutTemplate
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Icon mapping
const ICON_MAP = {
  FileText, CreditCard, UserPlus, Wrench, ShoppingBag, CheckCircle,
  AlertTriangle, Bell, Calendar, Gift, Heart, Star, Zap, Award,
  Briefcase, Clock, DollarSign, Mail, MessageSquare, Phone, Settings,
  Shield, Truck, Users, Wifi, Home, Check
};

// Available colors
const COLORS = [
  { name: 'Red', value: 'bg-red-500' },
  { name: 'Orange', value: 'bg-orange-500' },
  { name: 'Yellow', value: 'bg-yellow-500' },
  { name: 'Green', value: 'bg-green-500' },
  { name: 'Emerald', value: 'bg-emerald-500' },
  { name: 'Teal', value: 'bg-teal-500' },
  { name: 'Cyan', value: 'bg-cyan-500' },
  { name: 'Blue', value: 'bg-blue-500' },
  { name: 'Indigo', value: 'bg-indigo-500' },
  { name: 'Purple', value: 'bg-purple-500' },
  { name: 'Pink', value: 'bg-pink-500' },
  { name: 'Rose', value: 'bg-rose-500' }
];

// Category options
const CATEGORIES = ['Payment', 'Onboarding', 'Billing', 'Support', 'Sales', 'Notification', 'Custom'];

// Generate avatar colors based on phone number
const getAvatarColor = (id) => {
  if (!id) return 'from-gray-500 to-gray-600';
  const colors = [
    'from-emerald-400 to-teal-500',
    'from-blue-400 to-indigo-500',
    'from-purple-400 to-pink-500',
    'from-orange-400 to-red-500',
    'from-cyan-400 to-blue-500',
    'from-green-400 to-emerald-500',
    'from-pink-400 to-rose-500',
    'from-yellow-400 to-orange-500',
  ];
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Get initials from name or phone
const getInitials = (name, id) => {
  if (name && name.length > 0 && !name.match(/^\d+$/)) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }
  if (id) {
    const phone = id.split('@')[0];
    return phone.slice(-2);
  }
  return '??';
};

const WhatsAppDashboard = () => {
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [showVoiceCallModal, setShowVoiceCallModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showContacts, setShowContacts] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Bulk message state
  const [bulkSelectedContacts, setBulkSelectedContacts] = useState([]);
  const [bulkMessage, setBulkMessage] = useState('');
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const templateAttachmentRef = useRef(null);

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'Custom',
    message: '',
    color: 'bg-blue-500',
    icon: 'FileText',
    has_attachment: false,
    attachment_type: null,
    attachment_name: null,
    attachment_data: null
  });

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('monitoringAdminToken');
    if (!token) {
      navigate('/monitoring-dashboard/login');
    }
  }, [navigate]);

  // Poll for QR code and status
  useEffect(() => {
    checkStatus();
    const interval = setInterval(() => {
      if (connectionState !== 'connected') {
        checkStatus();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [connectionState]);

  // Poll for conversations when connected
  useEffect(() => {
    if (connectionState === 'connected') {
      fetchConversations();
      fetchContacts();
      const interval = setInterval(fetchConversations, 5000);
      return () => clearInterval(interval);
    }
  }, [connectionState]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for messages when conversation is selected
  useEffect(() => {
    if (selectedConversation && connectionState === 'connected') {
      fetchMessages(selectedConversation.id);
      const interval = setInterval(() => fetchMessages(selectedConversation.id), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation, connectionState]);

  // Fetch templates when modal opens
  useEffect(() => {
    if (showTemplatesModal) {
      fetchTemplates();
    }
  }, [showTemplatesModal]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('monitoringAdminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const checkStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/whatsapp-baileys/qr`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        navigate('/monitoring-dashboard/login');
        return;
      }
      
      if (response.status === 503) {
        setConnectionState('service_unavailable');
        return;
      }
      
      const data = await response.json();
      setQrCode(data.qr);
      setConnectionState(data.state);
      setUserInfo(data.userInfo);
    } catch (error) {
      console.error('Error checking status:', error);
      setConnectionState('error');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp-baileys/conversations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp-baileys/contacts`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setContacts(data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp-baileys/messages/${encodeURIComponent(conversationId)}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Transform media URLs to go through our backend proxy
          const messages = (data.messages || []).map(msg => {
            if (msg.mediaUrl && msg.mediaUrl.startsWith('/media/')) {
              // Convert /media/filename to full proxied URL
              const filename = msg.mediaUrl.replace('/media/', '');
              return {
                ...msg,
                mediaUrl: `${API_URL}/api/whatsapp-baileys/media/${filename}`
              };
            }
            return msg;
          });
          setMessages(messages);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp-templates/list`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || newMessage;
    if (!text.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/whatsapp-baileys/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ to: selectedConversation.id, message: text.trim() })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(selectedConversation.id);
        toast.success('Message sent!');
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error sending message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('to', selectedConversation.id);
      formData.append('caption', '');

      const response = await fetch(`${API_URL}/api/whatsapp-baileys/send-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        toast.success('Image sent!');
        fetchMessages(selectedConversation.id);
      } else {
        toast.error('Failed to send image');
      }
    } catch (error) {
      toast.error('Error sending image');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleSendDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('to', selectedConversation.id);
      formData.append('caption', '');

      const response = await fetch(`${API_URL}/api/whatsapp-baileys/send-document`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        toast.success('Document sent!');
        fetchMessages(selectedConversation.id);
      } else {
        toast.error('Failed to send document');
      }
    } catch (error) {
      toast.error('Error sending document');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleNewChat = async () => {
    if (!newChatPhone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setCreatingChat(true);
    try {
      const response = await fetch(`${API_URL}/api/whatsapp-baileys/new-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ phone: newChatPhone.trim(), message: newChatMessage.trim() || null })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Chat created!');
        setShowNewChatModal(false);
        setNewChatPhone('');
        setNewChatMessage('');
        fetchConversations();
        if (data.conversation) setSelectedConversation(data.conversation);
      } else {
        toast.error(data.error || 'Failed to create chat');
      }
    } catch (error) {
      toast.error('Error creating chat');
    } finally {
      setCreatingChat(false);
    }
  };

  // Bulk message functions
  const handleSendBulkMessage = async () => {
    if (bulkSelectedContacts.length === 0) {
      toast.error('Please select at least one contact');
      return;
    }
    if (!bulkMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingBulk(true);
    let successCount = 0;
    let failCount = 0;

    for (const contact of bulkSelectedContacts) {
      try {
        const response = await fetch(`${API_URL}/api/whatsapp-baileys/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ to: contact.id, message: bulkMessage.trim() })
        });

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
        // Small delay between messages to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        failCount++;
      }
    }

    setSendingBulk(false);
    setShowBulkMessageModal(false);
    setBulkSelectedContacts([]);
    setBulkMessage('');
    
    if (successCount > 0) {
      toast.success(`Sent to ${successCount} contacts successfully!`);
    }
    if (failCount > 0) {
      toast.error(`Failed to send to ${failCount} contacts`);
    }
  };

  const toggleBulkContact = (conv) => {
    setBulkSelectedContacts(prev => {
      const exists = prev.find(c => c.id === conv.id);
      if (exists) {
        return prev.filter(c => c.id !== conv.id);
      }
      return [...prev, conv];
    });
  };

  const selectAllBulkContacts = () => {
    const filtered = getFilteredBulkConversations();
    setBulkSelectedContacts(filtered);
  };

  const clearAllBulkContacts = () => {
    setBulkSelectedContacts([]);
  };

  const getFilteredBulkConversations = () => {
    return conversations.filter(conv => {
      if (!bulkSearchTerm) return true;
      const name = conv.name || '';
      const phone = conv.id.split('@')[0];
      return name.toLowerCase().includes(bulkSearchTerm.toLowerCase()) || phone.includes(bulkSearchTerm);
    });
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      setNewMessage(selectedTemplate.message);
      setShowTemplatesModal(false);
      setSelectedTemplate(null);
    }
  };

  const handleUseBulkTemplate = () => {
    if (selectedTemplate) {
      setBulkMessage(selectedTemplate.message);
      setShowTemplatesModal(false);
      setSelectedTemplate(null);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      category: 'Custom',
      message: `📋 *YOUR TEMPLATE TITLE*
━━━━━━━━━━━━━━━━━━━━━

Dear *{customer_name}*,

Your message content goes here.

┌─────────────────────────┐
│  📋 *SECTION TITLE*
├─────────────────────────┤
│  Field 1: {value1}
│  Field 2: {value2}
└─────────────────────────┘

*Important Notes:*
━━━━━━━━━━━━━━━━━━━━━
• Point 1
• Point 2
• Point 3

━━━━━━━━━━━━━━━━━━━━━
*NETIX FIBERNET PVT. LTD.*
📞 +91 93199 19988
☎️ Office Phone: 06276 - 459015
📧 support@netixfibernet.com
🌐 www.netixfibernet.com
📍 Hyderabad, Telangana
━━━━━━━━━━━━━━━━━━━━━

_Thank you!_ 🙏`,
      color: 'bg-blue-500',
      icon: 'FileText',
      has_attachment: false,
      attachment_type: null,
      attachment_name: null,
      attachment_data: null
    });
    setShowTemplateEditor(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      category: template.category,
      message: template.message,
      color: template.color,
      icon: template.icon,
      has_attachment: template.has_attachment,
      attachment_type: template.attachment_type,
      attachment_name: template.attachment_name,
      attachment_data: template.attachment_url
    });
    setShowTemplateEditor(true);
  };

  const handleDeleteTemplate = async (template) => {
    if (template.is_default) {
      toast.error('Cannot delete default templates');
      return;
    }

    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(`${API_URL}/api/whatsapp-templates/${template.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Template deleted!');
        fetchTemplates();
        if (selectedTemplate?.id === template.id) setSelectedTemplate(null);
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      toast.error('Error deleting template');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.message.trim()) {
      toast.error('Please fill in template name and message');
      return;
    }

    setSavingTemplate(true);
    try {
      const url = editingTemplate
        ? `${API_URL}/api/whatsapp-templates/${editingTemplate.id}`
        : `${API_URL}/api/whatsapp-templates/create`;
      
      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated!' : 'Template created!');
        setShowTemplateEditor(false);
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      toast.error('Error saving template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleTemplateAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const isImage = file.type.startsWith('image/');
      setTemplateForm(prev => ({
        ...prev,
        has_attachment: true,
        attachment_type: isImage ? 'image' : 'document',
        attachment_name: file.name,
        attachment_data: event.target.result
      }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeTemplateAttachment = () => {
    setTemplateForm(prev => ({
      ...prev,
      has_attachment: false,
      attachment_type: null,
      attachment_name: null,
      attachment_data: null
    }));
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/whatsapp-baileys/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      setConnectionState('disconnected');
      setUserInfo(null);
      setConversations([]);
      setMessages([]);
      setSelectedConversation(null);
      toast.success('Logged out');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatPhoneNumber = (jid) => {
    if (!jid) return '';
    const phone = jid.split('@')[0];
    
    // Handle Indian numbers (91XXXXXXXXXX - 12 digits)
    if (phone.length === 12 && phone.startsWith('91')) {
      return `+91 ${phone.slice(2, 7)} ${phone.slice(7)}`;
    }
    // Handle Indian numbers without leading 91 (10 digits)
    if (phone.length === 10) {
      return `+91 ${phone.slice(0, 5)} ${phone.slice(5)}`;
    }
    // Handle numbers with country code (starts with + or just digits > 10)
    if (phone.length > 10) {
      // Format as +XX XXXXX XXXXX for most international numbers
      const countryCodeLength = phone.length - 10;
      return `+${phone.slice(0, countryCodeLength)} ${phone.slice(countryCodeLength, countryCodeLength + 5)} ${phone.slice(countryCodeLength + 5)}`;
    }
    // Fallback - just show the number as is
    return phone;
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    const name = conv.name || '';
    const phone = formatPhoneNumber(conv.id);
    return name.toLowerCase().includes(searchTerm.toLowerCase()) || phone.includes(searchTerm);
  });

  const getIcon = (iconName) => ICON_MAP[iconName] || FileText;

  // Helper function to render WhatsApp-formatted text
  const renderFormattedText = (text) => {
    if (!text) return '';
    
    // Process WhatsApp formatting
    let formatted = text
      // Bold: *text* -> <strong>text</strong>
      .replace(/\*([^*]+)\*/g, '<strong class="font-bold">$1</strong>')
      // Italic: _text_ -> <em>text</em>
      .replace(/_([^_]+)_/g, '<em class="italic">$1</em>')
      // Strikethrough: ~text~ -> <del>text</del>
      .replace(/~([^~]+)~/g, '<del class="line-through">$1</del>')
      // Monospace: ```text``` -> <code>text</code>
      .replace(/```([^`]+)```/g, '<code class="bg-black/30 px-1 rounded font-mono text-sm">$1</code>');
    
    return formatted;
  };

  // Service unavailable view
  if (connectionState === 'service_unavailable') {
    return (
      <div className="min-h-screen bg-[#111b21] flex items-center justify-center">
        <Card className="bg-[#202c33] border-[#2a3942] max-w-md">
          <CardContent className="p-8 text-center">
            <WifiOff className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Service Unavailable</h2>
            <p className="text-gray-400 mb-4">WhatsApp service is not running.</p>
            <Button onClick={checkStatus} className="bg-[#00a884] hover:bg-[#008f72]">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111b21]">
      {/* Header */}
      <div className="bg-[#202c33] border-b border-[#2a3942] px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left section */}
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/monitoring-dashboard')} variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-[#2a3942]">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="h-6 w-px bg-[#2a3942]"></div>
            <MessageSquare className="h-6 w-6 text-[#00a884]" />
            <span className="text-white font-semibold text-lg">WhatsApp Dashboard</span>
            
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
              connectionState === 'connected' ? 'bg-green-500/20 text-green-400' 
              : connectionState === 'qr_ready' ? 'bg-yellow-500/20 text-yellow-400'
              : 'bg-red-500/20 text-red-400'
            }`}>
              {connectionState === 'connected' ? <><Wifi className="h-3.5 w-3.5" />Connected</>
              : connectionState === 'qr_ready' ? <><QrCode className="h-3.5 w-3.5" />Scan QR</>
              : <><WifiOff className="h-3.5 w-3.5" />Disconnected</>}
            </div>
          </div>
          
          {/* Center section - Feature buttons */}
          {connectionState === 'connected' && (
            <div className="flex items-center gap-2">
              {/* Voice Call Button */}
              <Button 
                onClick={() => setShowVoiceCallModal(true)} 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#2a3942] flex items-center gap-2 border border-[#2a3942] rounded-lg px-4"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                  <PhoneCall className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Voice Call</span>
              </Button>

              {/* Bulk Message Button */}
              <Button 
                onClick={() => setShowBulkMessageModal(true)} 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#2a3942] flex items-center gap-2 border border-[#2a3942] rounded-lg px-4"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center">
                  <Megaphone className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Bulk Message</span>
              </Button>

              {/* Templates Button */}
              <Button 
                onClick={() => setShowTemplatesModal(true)} 
                variant="ghost" 
                size="sm" 
                className="text-gray-300 hover:text-white hover:bg-[#2a3942] flex items-center gap-2 border border-[#2a3942] rounded-lg px-4"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
                  <LayoutTemplate className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm">Templates</span>
              </Button>
            </div>
          )}

          {/* Right section */}
          <div className="flex items-center gap-3">
            {/* Netix Fibernet Logo */}
            <div className="flex items-center gap-2 bg-[#111b21] px-3 py-1.5 rounded-lg border border-[#2a3942]">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00a884] to-[#008f72] flex items-center justify-center">
                <span className="text-white font-bold text-xs">NF</span>
              </div>
              <span className="text-white font-semibold text-sm">Netix Fibernet</span>
            </div>
            
            <Button onClick={checkStatus} variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-[#2a3942]">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {connectionState === 'connected' && (
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                <LogOut className="h-4 w-4 mr-1" />Logout
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-56px)]">
        {connectionState === 'connected' ? (
          <>
            {/* Conversations List */}
            <div className="w-96 bg-[#111b21] border-r border-[#2a3942] flex flex-col">
              <div className="p-3 border-b border-[#2a3942] flex items-center justify-between">
                <h3 className="text-white font-semibold">Chats ({conversations.length})</h3>
                <Button onClick={() => setShowNewChatModal(true)} size="sm" className="bg-[#00a884] hover:bg-[#008f72] text-white">
                  <Plus className="h-4 w-4 mr-1" />New Chat
                </Button>
              </div>
              
              <div className="p-2 border-b border-[#2a3942]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input placeholder="Search chats..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[#2a3942] border-0 text-white placeholder:text-gray-400 focus-visible:ring-0" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filteredConversations.length === 0 ? (
                  <div className="p-6 text-center text-gray-400">
                    <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p className="font-semibold mb-2">No conversations yet</p>
                    <Button onClick={() => setShowNewChatModal(true)} className="bg-[#00a884] hover:bg-[#008f72]">
                      <Plus className="h-4 w-4 mr-2" />Start New Chat
                    </Button>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <div key={conv.id} onClick={() => setSelectedConversation(conv)}
                      className={`p-3 cursor-pointer hover:bg-[#202c33] border-b border-[#2a3942]/50 ${selectedConversation?.id === conv.id ? 'bg-[#2a3942]' : ''}`}>
                      <div className="flex items-center gap-3">
                        {/* Improved Avatar */}
                        <div className="relative flex-shrink-0">
                          {conv.profilePic ? (
                            <img src={conv.profilePic} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#2a3942]"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          ) : null}
                          <div 
                            className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(conv.id)} flex items-center justify-center ring-2 ring-[#2a3942] shadow-lg`}
                            style={{ display: conv.profilePic ? 'none' : 'flex' }}
                          >
                            <span className="text-white font-bold text-sm">{getInitials(conv.name, conv.id)}</span>
                          </div>
                          {/* Online indicator */}
                          <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-[#111b21]"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-medium truncate">{conv.name || formatPhoneNumber(conv.id)}</span>
                            <span className="text-xs text-gray-400">{formatTime(conv.lastMessageTime)}</span>
                          </div>
                          <p className="text-sm text-gray-400 truncate">{conv.lastMessageText || 'No messages'}</p>
                        </div>
                        {conv.unreadCount > 0 && (
                          <div className="bg-[#00a884] text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 font-bold">
                            {conv.unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col bg-[#0b141a]">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="bg-[#202c33] px-4 py-3 border-b border-[#2a3942]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Avatar in header */}
                        <div className="relative">
                          {selectedConversation.profilePic ? (
                            <img src={selectedConversation.profilePic} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-[#00a884]"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          ) : null}
                          <div 
                            className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(selectedConversation.id)} flex items-center justify-center ring-2 ring-[#00a884]`}
                            style={{ display: selectedConversation.profilePic ? 'none' : 'flex' }}
                          >
                            <span className="text-white font-bold text-sm">{getInitials(selectedConversation.name, selectedConversation.id)}</span>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{selectedConversation.name || formatPhoneNumber(selectedConversation.id)}</h3>
                          <p className="text-xs text-gray-400">{formatPhoneNumber(selectedConversation.id)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button onClick={() => setShowVoiceCallModal(true)} variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-[#2a3942]">
                          <PhoneCall className="h-5 w-5" />
                        </Button>
                        <Button onClick={() => setShowTemplatesModal(true)} variant="ghost" size="sm" className="text-[#00a884] hover:text-[#00a884] hover:bg-[#00a884]/10">
                          <FileText className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23182229\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
                  }}>
                    {messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-gray-400">
                          <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No messages yet</p>
                        </div>
                      </div>
                    ) : (
                      messages.map((msg, index) => (
                        <div key={msg.id || index} className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] rounded-lg px-3 py-2 shadow ${msg.fromMe ? 'bg-[#005c4b] text-white' : 'bg-[#202c33] text-white'}`}>
                            {/* Render image if present */}
                            {msg.type === 'image' && msg.mediaUrl && (
                              <div className="mb-2">
                                <img 
                                  src={msg.mediaUrl} 
                                  alt="Image" 
                                  className="max-w-full rounded-lg cursor-pointer hover:opacity-90"
                                  onClick={() => window.open(msg.mediaUrl, '_blank')}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                  }}
                                />
                                <div className="hidden items-center justify-center p-4 bg-[#111b21] rounded-lg">
                                  <Image className="h-8 w-8 text-gray-400" />
                                  <span className="ml-2 text-gray-400 text-sm">Image unavailable</span>
                                </div>
                              </div>
                            )}
                            {/* Render document if present */}
                            {msg.type === 'document' && (
                              <div className="flex items-center gap-2 p-2 bg-[#111b21] rounded-lg mb-2">
                                <FileText className="h-8 w-8 text-[#00a884]" />
                                <span className="text-sm text-gray-300">{msg.text}</span>
                              </div>
                            )}
                            {/* Text content */}
                            <p className="break-words whitespace-pre-wrap">{msg.type === 'image' ? (msg.caption || msg.text) : msg.text}</p>
                            <div className={`flex items-center gap-1 mt-1 ${msg.fromMe ? 'justify-end' : ''}`}>
                              <span className="text-[10px] text-gray-400">{formatTime(msg.timestamp)}</span>
                              {msg.fromMe && <CheckCheck className="h-3 w-3 text-[#53bdeb]" />}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="bg-[#202c33] p-3 border-t border-[#2a3942]">
                    <div className="flex items-center gap-2">
                      <input type="file" ref={imageInputRef} accept="image/*" className="hidden" onChange={handleSendImage} />
                      <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" className="hidden" onChange={handleSendDocument} />
                      
                      <Button onClick={() => imageInputRef.current?.click()} variant="ghost" size="sm" disabled={uploadingFile}
                        className="text-gray-400 hover:text-white hover:bg-[#2a3942]" title="Send Image">
                        <Image className="h-5 w-5" />
                      </Button>
                      
                      <Button onClick={() => fileInputRef.current?.click()} variant="ghost" size="sm" disabled={uploadingFile}
                        className="text-gray-400 hover:text-white hover:bg-[#2a3942]" title="Send Document">
                        <Paperclip className="h-5 w-5" />
                      </Button>

                      <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                        placeholder="Type a message" className="flex-1 bg-[#2a3942] border-0 text-white placeholder:text-gray-400 focus-visible:ring-0" />
                      <Button onClick={() => sendMessage()} disabled={sendingMessage || uploadingFile || !newMessage.trim()}
                        className="bg-[#00a884] hover:bg-[#008f72] disabled:opacity-50">
                        {sendingMessage || uploadingFile ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#00a884] to-[#008f72] flex items-center justify-center">
                      <MessageSquare className="h-12 w-12 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-2 text-white">Netix WhatsApp Dashboard</h3>
                    <p className="mb-4">Select a conversation or start a new chat</p>
                    <Button onClick={() => setShowNewChatModal(true)} className="bg-[#00a884] hover:bg-[#008f72]">
                      <Plus className="h-4 w-4 mr-2" />Start New Chat
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          /* QR Code Screen */
          <div className="flex-1 flex items-center justify-center">
            <Card className="bg-[#202c33] border-[#2a3942] w-[420px]">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-gradient-to-br from-[#00a884] to-[#008f72] flex items-center justify-center shadow-lg">
                  <MessageSquare className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-white text-2xl">Connect WhatsApp</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                {loading ? (
                  <div className="py-8">
                    <Loader2 className="h-12 w-12 animate-spin text-[#00a884] mx-auto mb-4" />
                    <p className="text-gray-400">Connecting...</p>
                  </div>
                ) : qrCode ? (
                  <div>
                    <div className="bg-white p-4 rounded-lg inline-block mb-4 shadow-lg">
                      <img src={qrCode} alt="QR Code" className="w-64 h-64" />
                    </div>
                    <div className="text-left bg-[#111b21] rounded-lg p-4 mb-4">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <Phone className="h-4 w-4 text-[#00a884]" />How to Link
                      </h4>
                      <ol className="text-gray-400 text-sm space-y-2">
                        <li><span className="text-[#00a884] font-bold">1.</span> Open WhatsApp on your phone</li>
                        <li><span className="text-[#00a884] font-bold">2.</span> Go to Settings → Linked Devices</li>
                        <li><span className="text-[#00a884] font-bold">3.</span> Tap Link a Device</li>
                        <li><span className="text-[#00a884] font-bold">4.</span> Scan this QR code</li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div className="py-8">
                    <WifiOff className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <Button onClick={checkStatus} className="bg-[#00a884] hover:bg-[#008f72]">
                      <RefreshCw className="h-4 w-4 mr-2" />Try Again
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Voice Call Modal */}
      {showVoiceCallModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="bg-[#202c33] border-[#2a3942] w-[400px]">
            <CardHeader className="border-b border-[#2a3942]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <PhoneCall className="h-5 w-5 text-green-400" />Voice Call
                </CardTitle>
                <Button onClick={() => setShowVoiceCallModal(false)} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                <PhoneCall className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-white text-lg font-semibold mb-2">Voice Call Feature</h3>
              <p className="text-gray-400 mb-6">
                Voice and video calls are available through your linked WhatsApp. Use your phone's WhatsApp app to make calls.
              </p>
              <div className="space-y-3">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <Phone className="h-4 w-4 mr-2" />Single Call
                </Button>
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <Users className="h-4 w-4 mr-2" />Group Call
                </Button>
              </div>
              <p className="text-gray-500 text-xs mt-4">
                Note: Calls are made through the linked WhatsApp device
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Message Modal */}
      {showBulkMessageModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="bg-[#202c33] border-[#2a3942] w-[700px] max-h-[85vh] overflow-hidden">
            <CardHeader className="border-b border-[#2a3942]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-blue-400" />Bulk WhatsApp Message
                </CardTitle>
                <Button onClick={() => { setShowBulkMessageModal(false); setBulkSelectedContacts([]); setBulkMessage(''); }} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex h-[500px]">
              {/* Contact Selection */}
              <div className="w-1/2 border-r border-[#2a3942] flex flex-col">
                <div className="p-3 border-b border-[#2a3942]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-semibold">Select Contacts ({bulkSelectedContacts.length})</span>
                    <div className="flex gap-2">
                      <Button onClick={selectAllBulkContacts} size="sm" variant="ghost" className="text-[#00a884] text-xs">Select All</Button>
                      <Button onClick={clearAllBulkContacts} size="sm" variant="ghost" className="text-red-400 text-xs">Clear</Button>
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input placeholder="Search contacts..." value={bulkSearchTerm} onChange={(e) => setBulkSearchTerm(e.target.value)}
                      className="pl-10 bg-[#2a3942] border-0 text-white placeholder:text-gray-400 focus-visible:ring-0 h-9" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {getFilteredBulkConversations().map((conv) => {
                    const isSelected = bulkSelectedContacts.find(c => c.id === conv.id);
                    return (
                      <div key={conv.id} onClick={() => toggleBulkContact(conv)}
                        className={`p-2 cursor-pointer hover:bg-[#2a3942] border-b border-[#2a3942]/50 flex items-center gap-2 ${isSelected ? 'bg-[#00a884]/20' : ''}`}>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-[#00a884] border-[#00a884]' : 'border-gray-500'}`}>
                          {isSelected && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarColor(conv.id)} flex items-center justify-center`}>
                          <span className="text-white font-bold text-xs">{getInitials(conv.name, conv.id)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{conv.name || formatPhoneNumber(conv.id)}</p>
                          <p className="text-gray-400 text-xs truncate">{formatPhoneNumber(conv.id)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Message Composition */}
              <div className="w-1/2 flex flex-col">
                <div className="p-3 border-b border-[#2a3942]">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">Compose Message</span>
                    <Button onClick={() => { fetchTemplates(); setShowTemplatesModal(true); }} size="sm" variant="ghost" className="text-[#00a884]">
                      <LayoutTemplate className="h-4 w-4 mr-1" />Use Template
                    </Button>
                  </div>
                </div>
                <div className="flex-1 p-3 flex flex-col">
                  <Textarea 
                    value={bulkMessage} 
                    onChange={(e) => setBulkMessage(e.target.value)}
                    placeholder="Type your message here..."
                    className="flex-1 bg-[#2a3942] border-0 text-white placeholder:text-gray-400 focus-visible:ring-0 resize-none"
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-gray-400 text-sm">{bulkMessage.length} characters</span>
                    <Button 
                      onClick={handleSendBulkMessage} 
                      disabled={sendingBulk || bulkSelectedContacts.length === 0 || !bulkMessage.trim()}
                      className="bg-[#00a884] hover:bg-[#008f72]"
                    >
                      {sendingBulk ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" />Send to {bulkSelectedContacts.length} contacts</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="bg-[#202c33] border-[#2a3942] w-[400px]">
            <CardHeader className="border-b border-[#2a3942]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="h-5 w-5 text-[#00a884]" />New Chat
                </CardTitle>
                <Button onClick={() => setShowNewChatModal(false)} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Phone Number *</label>
                <Input placeholder="e.g., 919876543210" value={newChatPhone} onChange={(e) => setNewChatPhone(e.target.value)}
                  className="bg-[#2a3942] border-0 text-white placeholder:text-gray-500 focus-visible:ring-0" />
                <p className="text-gray-500 text-xs mt-1">Include country code</p>
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-2 block">First Message</label>
                <Input placeholder="Type message..." value={newChatMessage} onChange={(e) => setNewChatMessage(e.target.value)}
                  className="bg-[#2a3942] border-0 text-white placeholder:text-gray-500 focus-visible:ring-0" />
              </div>
              <Button onClick={handleNewChat} disabled={creatingChat || !newChatPhone.trim()} className="w-full bg-[#00a884] hover:bg-[#008f72]">
                {creatingChat ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : <><MessageSquare className="h-4 w-4 mr-2" />Start Chat</>}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="bg-[#202c33] border-[#2a3942] w-[850px] max-h-[85vh] overflow-hidden">
            <CardHeader className="border-b border-[#2a3942]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <LayoutTemplate className="h-5 w-5 text-purple-400" />Message Templates
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button onClick={handleCreateTemplate} size="sm" className="bg-[#00a884] hover:bg-[#008f72]">
                    <Plus className="h-4 w-4 mr-1" />New Template
                  </Button>
                  <Button onClick={() => { setShowTemplatesModal(false); setSelectedTemplate(null); }} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex h-[550px]">
              {/* Template List */}
              <div className="w-72 border-r border-[#2a3942] overflow-y-auto">
                {templates.map((template) => {
                  const Icon = getIcon(template.icon);
                  return (
                    <div key={template.id} onClick={() => handleSelectTemplate(template)}
                      className={`p-3 cursor-pointer hover:bg-[#2a3942] border-b border-[#2a3942] ${selectedTemplate?.id === template.id ? 'bg-[#2a3942]' : ''}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${template.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{template.name}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">{template.category}</span>
                            {template.is_default && <span className="text-[#00a884] text-xs">Default</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Template Preview */}
              <div className="flex-1 flex flex-col">
                {selectedTemplate ? (
                  <>
                    <div className="p-4 border-b border-[#2a3942] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {(() => { const Icon = getIcon(selectedTemplate.icon); return (
                          <div className={`w-10 h-10 rounded-full ${selectedTemplate.color} flex items-center justify-center shadow-lg`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                        ); })()}
                        <div>
                          <h3 className="text-white font-semibold">{selectedTemplate.name}</h3>
                          <p className="text-gray-400 text-sm">{selectedTemplate.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!selectedTemplate.is_default && (
                          <>
                            <Button onClick={() => handleEditTemplate(selectedTemplate)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button onClick={() => handleDeleteTemplate(selectedTemplate)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button onClick={showBulkMessageModal ? handleUseBulkTemplate : handleUseTemplate} className="bg-[#00a884] hover:bg-[#008f72]">Use Template</Button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 bg-[#0b141a]" style={{
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23182229\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
                    }}>
                      <div className="flex justify-end">
                        <div className="max-w-[85%] rounded-lg overflow-hidden shadow-xl">
                          {/* Template header with icon and color */}
                          <div className={`${selectedTemplate.color} px-4 py-2 flex items-center gap-2`}>
                            {(() => { const Icon = getIcon(selectedTemplate.icon); return <Icon className="h-4 w-4 text-white" />; })()}
                            <span className="text-white font-medium text-sm">{selectedTemplate.name}</span>
                          </div>
                          {/* Message content */}
                          <div className="bg-[#005c4b] px-4 py-3">
                            <div 
                              className="text-white text-sm whitespace-pre-wrap leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: renderFormattedText(selectedTemplate.message) }}
                            />
                            {/* Attachment preview */}
                            {selectedTemplate.has_attachment && (
                              <div className="mt-3 pt-3 border-t border-white/20">
                                <div className="flex items-center gap-2 text-white/80 text-xs">
                                  {selectedTemplate.attachment_type === 'image' ? (
                                    <><Image className="h-4 w-4" /><span>Image attached</span></>
                                  ) : (
                                    <><FileText className="h-4 w-4" /><span>{selectedTemplate.attachment_name || 'Document attached'}</span></>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Timestamp */}
                            <div className="flex justify-end mt-2">
                              <span className="text-[10px] text-white/60">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                              <CheckCheck className="h-3 w-3 text-[#53bdeb] ml-1" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <LayoutTemplate className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Select a template to preview</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="bg-[#202c33] border-[#2a3942] w-[900px] max-h-[90vh] overflow-hidden">
            <CardHeader className="border-b border-[#2a3942]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">{editingTemplate ? 'Edit Template' : 'Create New Template'}</CardTitle>
                <Button onClick={() => setShowTemplateEditor(false)} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex h-[600px]">
              {/* Editor Form */}
              <div className="w-1/2 p-4 border-r border-[#2a3942] overflow-y-auto space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Template Name *</label>
                  <Input value={templateForm.name} onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Payment Reminder" className="bg-[#2a3942] border-0 text-white placeholder:text-gray-500 focus-visible:ring-0" />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Category</label>
                  <select value={templateForm.category} onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full bg-[#2a3942] border-0 text-white rounded-md p-2 focus:ring-0">
                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Icon</label>
                    <select value={templateForm.icon} onChange={(e) => setTemplateForm(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full bg-[#2a3942] border-0 text-white rounded-md p-2 focus:ring-0">
                      {Object.keys(ICON_MAP).map(icon => <option key={icon} value={icon}>{icon}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Color</label>
                    <select value={templateForm.color} onChange={(e) => setTemplateForm(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full bg-[#2a3942] border-0 text-white rounded-md p-2 focus:ring-0">
                      {COLORS.map(c => <option key={c.value} value={c.value}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Message Content *</label>
                  <Textarea value={templateForm.message} onChange={(e) => setTemplateForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Enter your template message..." rows={12}
                    className="bg-[#2a3942] border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 font-mono text-sm" />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Attachment (Optional)</label>
                  <input type="file" ref={templateAttachmentRef} accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleTemplateAttachment} />
                  
                  {templateForm.has_attachment ? (
                    <div className="flex items-center gap-2 bg-[#111b21] p-3 rounded-lg">
                      {templateForm.attachment_type === 'image' ? (
                        <img src={templateForm.attachment_data} alt="" className="w-16 h-16 object-cover rounded" />
                      ) : (
                        <FileText className="h-10 w-10 text-[#00a884]" />
                      )}
                      <div className="flex-1">
                        <p className="text-white text-sm truncate">{templateForm.attachment_name}</p>
                      </div>
                      <Button onClick={removeTemplateAttachment} variant="ghost" size="sm" className="text-red-400">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => templateAttachmentRef.current?.click()} variant="outline" className="w-full border-dashed border-[#2a3942] text-gray-400 hover:text-white">
                      <Upload className="h-4 w-4 mr-2" />Add Attachment
                    </Button>
                  )}
                </div>

                <Button onClick={handleSaveTemplate} disabled={savingTemplate || !templateForm.name.trim() || !templateForm.message.trim()}
                  className="w-full bg-[#00a884] hover:bg-[#008f72]">
                  {savingTemplate ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingTemplate ? 'Update' : 'Create'} Template</>}
                </Button>
              </div>

              {/* Preview */}
              <div className="w-1/2 flex flex-col">
                <div className="p-4 border-b border-[#2a3942]">
                  <h3 className="text-white font-semibold">Live Preview</h3>
                  <p className="text-gray-400 text-xs mt-1">See how your template will look in WhatsApp</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 bg-[#0b141a]" style={{
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23182229\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
                }}>
                  <div className="flex justify-end">
                    <div className="max-w-[90%] rounded-lg overflow-hidden shadow-xl">
                      {/* Template header */}
                      {templateForm.name && (
                        <div className={`${templateForm.color} px-4 py-2 flex items-center gap-2`}>
                          {(() => { const Icon = getIcon(templateForm.icon); return <Icon className="h-4 w-4 text-white" />; })()}
                          <span className="text-white font-medium text-sm">{templateForm.name || 'Template Name'}</span>
                        </div>
                      )}
                      {/* Message content */}
                      <div className="bg-[#005c4b] px-4 py-3">
                        <div 
                          className="text-white text-sm whitespace-pre-wrap leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: renderFormattedText(templateForm.message) || '<span class="text-white/50">Your message will appear here...</span>' }}
                        />
                        {/* Attachment preview */}
                        {templateForm.has_attachment && (
                          <div className="mt-3 pt-3 border-t border-white/20">
                            {templateForm.attachment_type === 'image' && templateForm.attachment_data ? (
                              <img src={templateForm.attachment_data} alt="" className="max-w-full rounded-lg" />
                            ) : (
                              <div className="flex items-center gap-2 bg-[#111b21]/50 p-2 rounded">
                                <FileText className="h-6 w-6 text-[#00a884]" />
                                <span className="text-white/80 text-xs">{templateForm.attachment_name}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Timestamp */}
                        <div className="flex justify-end mt-2">
                          <span className="text-[10px] text-white/60">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                          <CheckCheck className="h-3 w-3 text-[#53bdeb] ml-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Formatting help */}
                <div className="p-3 border-t border-[#2a3942] bg-[#111b21]">
                  <p className="text-gray-400 text-xs mb-2 font-semibold">Formatting Tips:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-gray-500"><code className="bg-[#2a3942] px-1 rounded">*bold*</code> → <strong className="text-white">bold</strong></div>
                    <div className="text-gray-500"><code className="bg-[#2a3942] px-1 rounded">_italic_</code> → <em className="text-white">italic</em></div>
                    <div className="text-gray-500"><code className="bg-[#2a3942] px-1 rounded">~strike~</code> → <del className="text-white">strike</del></div>
                    <div className="text-gray-500"><code className="bg-[#2a3942] px-1 rounded">{'{placeholder}'}</code> → variable</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default WhatsAppDashboard;
